import React from 'react';

/**
 * PageErrorBoundary — per-page error containment.
 *
 * Wraps a single route/page so that if it crashes, only the page content
 * shows the error — the Navbar, Footer, and all other pages stay alive.
 *
 * Usage in App.js:
 *   <PageErrorBoundary>
 *     <MyPage />
 *   </PageErrorBoundary>
 */
// Google Translate wraps React-managed text nodes in <font> elements after the page loads.
// Any subsequent React re-render that touches those nodes throws an insertBefore/removeChild
// NotFoundError. We detect it here and reload silently (cookie-based GT re-applies cleanly on
// the fresh page). A sessionStorage counter prevents infinite reload loops.
function isGoogleTranslateError(error) {
    const msg = (error && error.message) ? error.message : '';
    return msg.includes('insertBefore') || msg.includes('removeChild');
}

class PageErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { crashed: false, error: null };
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError(error) {
        if (isGoogleTranslateError(error)) {
            // Suppress the error UI — componentDidCatch will reload the page
            return { crashed: false, error: null };
        }
        return { crashed: true, error };
    }

    componentDidCatch(error, info) {
        if (isGoogleTranslateError(error)) {
            const key = 'hm_gt_reload_count';
            const count = parseInt(sessionStorage.getItem(key) || '0', 10);
            if (count < 3) {
                sessionStorage.setItem(key, String(count + 1));
                window.location.reload();
            }
            return;
        }
        console.error('[PageErrorBoundary] Page crashed:', error, info?.componentStack);
    }

    // A crash used to stick: every page after it showed the error until a
    // full reload. Moving to another page (resetKey = the path) clears it.
    componentDidUpdate(prevProps) {
        if (this.state.crashed && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ crashed: false, error: null });
        }
    }

    handleRetry() {
        this.setState({ crashed: false, error: null });
    }

    render() {
        if (!this.state.crashed) return this.props.children;

        const isDev = process.env.NODE_ENV === 'development';

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                textAlign: 'center',
                padding: '2rem',
                fontFamily: 'var(--font-sans, sans-serif)',
            }}>
                {/* Icon */}
                <div style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'rgba(239,68,68,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.25rem',
                }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                        stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                </div>

                {/* Heading */}
                <h2 style={{
                    fontSize: '1.35rem',
                    fontWeight: 700,
                    color: 'var(--foreground, #0d1117)',
                    margin: '0 0 0.5rem',
                }}>
                    Something went wrong
                </h2>

                {/* Sub-text */}
                <p style={{
                    color: '#6b7280',
                    marginBottom: '1.5rem',
                    maxWidth: 400,
                    lineHeight: 1.6,
                }}>
                    This page encountered an unexpected error.
                    Try refreshing, or go back to the home page.
                </p>

                {/* Dev-only error detail */}
                {isDev && this.state.error && (
                    <pre style={{
                        background: '#fef2f2',
                        border: '1px solid #fca5a5',
                        borderRadius: 8,
                        padding: '0.75rem 1rem',
                        fontSize: '0.75rem',
                        color: '#b91c1c',
                        textAlign: 'left',
                        maxWidth: 560,
                        overflowX: 'auto',
                        marginBottom: '1.5rem',
                    }}>
                        {this.state.error.toString()}
                    </pre>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button
                        onClick={this.handleRetry}
                        style={{
                            background: 'var(--brand, #10b981)',
                            color: '#fff',
                            padding: '10px 24px',
                            borderRadius: 8,
                            fontWeight: 700,
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                        }}
                    >
                        Try again
                    </button>
                    <a
                        href="/"
                        style={{
                            background: 'transparent',
                            color: 'var(--foreground, #0d1117)',
                            padding: '10px 24px',
                            borderRadius: 8,
                            fontWeight: 600,
                            border: '1.5px solid #e5e7eb',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            textDecoration: 'none',
                        }}
                    >
                        Go to Home
                    </a>
                </div>
            </div>
        );
    }
}

export default PageErrorBoundary;
